<template>
  <section class="ann-milestone">
    <div class="ms-divider ms-divider--top"></div>
    <div class="ms-divider ms-divider--bottom"></div>

    <div class="ms-panel">
      <div class="ms-panel-inner"></div>
    </div>

    <!-- 标题跨在面板上沿，需在面板之后渲染 -->
    <div class="ms-title-wrap">
      <img class="ms-title" :class="`is-${titleLang}`" :src="titleImg" alt="milestone-title" />
    </div>

    <!-- 左侧进度轨道（设计稿为静态整条渐变，进度由圆点/对勾表示） -->
    <div class="ms-track-base"></div>
    <div class="ms-track-fill"></div>

    <template v-for="(row, i) in displayRows" :key="i">
      <div class="ms-row-bg" :style="rectStyle(row.card[0], row.card[1], ROW.width, ROW.height)"></div>
      <div
        class="ms-circle"
        :style="rectStyle(row.card[0] + ROW.circle.dx, row.card[1] + ROW.circle.dy, ROW.circle.size, ROW.circle.size)"
      ></div>
      <img
        v-if="row.rewardItem && isMilestoneItem(row.rewardItem)"
        class="ms-icon"
        :src="getItemIcon(getRewardId(row.rewardItem))"
        :alt="itemName(row.rewardItem)"
        :style="rewardIconStyle(row)"
      />
      <div
        class="ms-times"
        :class="{ achieved: row.status > 0 }"
        :style="rectStyle(row.label[0], row.label[1], 54, 37)"
      >
        {{ $t('ann2026.milestone.times').replace('_n', row.target) }}
      </div>
      <div
        class="ms-name flex-center"
        :style="rectStyle(row.card[0] + ROW.name.dx, row.card[1], ROW.name.width, ROW.height)"
      >
        <span>{{ row.rewardItem ? itemName(row.rewardItem) : $t(`ann2026.milestone.reward_${i + 1}`) }}</span>
        <span v-if="row.rewardItem">×{{ getRewardCount(row.rewardItem) }}</span>
      </div>
      <div
        class="ms-btn flex-center btn"
        :class="btnClass(row)"
        :style="rectStyle(row.card[0] + ROW.btn.dx, row.card[1] + ROW.btn.dy, ROW.btn.width, ROW.btn.height)"
        @click="onReceive(row)"
      >
        {{ btnText(row) }}
      </div>
      <img
        v-if="i !== checkIndex"
        class="ms-dot"
        :src="row.status > 0 ? dotOn : dotOff"
        :style="rectStyle(row.dot[0], row.dot[1], row.dot[2], row.dot[2])"
      />
    </template>

    <!-- 已达成最高档位的对勾，中心对齐该档圆点 -->
    <img v-if="checkIndex >= 0" class="ms-check" :src="checkImg" :style="checkStyle" />

    <div class="ms-confetti"></div>
  </section>
</template>

<script setup>
/* eslint-disable */
import { computed, defineProps, defineEmits } from 'vue'
import { useI18n } from 'vue-i18n'
import { SECTION_TOP, ROW, CHECK_SIZE, DESIGN_ROWS } from './const'
import { getItemIcon, isItemId } from '../../item'
import {
  getItemName,
  getRewardCount,
  getRewardId,
  parseProps,
} from '../../utils/reward'

import titleEn from '@/assets/imgs/act/ann2026/milestone/title-en.png'
import titleZh from '@/assets/imgs/act/ann2026/milestone/title-zh.png'
import titleVi from '@/assets/imgs/act/ann2026/milestone/title-vi.png'
import titleTh from '@/assets/imgs/act/ann2026/milestone/title-th.png'
import titleId from '@/assets/imgs/act/ann2026/milestone/title-id.png'

import dotOn from '@/assets/imgs/act/ann2026/milestone/dot-on.png'
import dotOff from '@/assets/imgs/act/ann2026/milestone/dot-off.png'
import checkImg from '@/assets/imgs/act/ann2026/milestone/check.png'

const props = defineProps({
  // /milestone/list 返回的 milestoneList：[{ rewardCode, targetCount, status }]
  list: { type: Array, default: () => [] },
})
const emit = defineEmits(['receive'])

const { t, te, locale } = useI18n()

const titleImg = computed(() => {
  switch (locale.value) {
    case 'vi':
      return titleVi
    case 'th':
      return titleTh
    case 'id':
      return titleId
    case 'tw':
      return titleZh
    case 'hk':
      return titleZh
    case 'en':
      return titleEn
    default:
      return titleEn
  }
})

const titleLang = computed(() => {
  switch (locale.value) {
    case 'vi':
      return 'vi'
    case 'th':
      return 'th'
    case 'id':
      return 'id'
    case 'tw':
      return 'zh'
    case 'hk':
      return 'zh'
    default:
      return 'en'
  }
})

// 设计稿页面绝对坐标 -> 区块内绝对定位（rem 基准与 postcss rootValue 75.2 一致）
const rem = (px) => `${+(px / 75.2).toFixed(5)}rem`
const rectStyle = (left, top, width, height) => ({
  left: rem(left),
  top: rem(top - SECTION_TOP),
  width: rem(width),
  height: rem(height),
})

const sortedList = computed(() =>
  [...props.list].sort((a, b) => (a.targetCount ?? 0) - (b.targetCount ?? 0)),
)

const milestoneRewards = (milestone) => {
  if (!milestone) return []

  const fields = [
    milestone.propSnapshot,
    milestone.propInfo,
    milestone.rewardItems,
    milestone.rewards,
  ]

  for (const field of fields) {
    const rewards = parseProps(field)
    if (rewards.length) return rewards
  }

  return isItemId(getRewardId(milestone)) ? [milestone] : []
}

// 设计稿 11 档与接口数据按序合并；未登录/无数据时全部为未达成
const displayRows = computed(() =>
  DESIGN_ROWS.map((row, i) => {
    const api = sortedList.value[i]
    const rewards = milestoneRewards(api)
    return {
      ...row,
      rewardCode: api?.rewardCode,
      rewardItem: rewards[0] || row.reward || null,
      target: api?.targetCount ?? row.target,
      status: api?.status ?? 0,
    }
  }),
)

const checkIndex = computed(() => {
  let idx = -1
  displayRows.value.forEach((row, i) => {
    if (row.status > 0) idx = i
  })
  return idx
})

const checkStyle = computed(() => {
  const dot = displayRows.value[checkIndex.value].dot
  const cx = dot[0] + dot[2] / 2
  const cy = dot[1] + dot[2] / 2
  return rectStyle(cx - CHECK_SIZE / 2, cy - CHECK_SIZE / 2, CHECK_SIZE, CHECK_SIZE)
})

const itemName = (reward) =>
  getItemName(getRewardId(reward), t, te)

const isMilestoneItem = (reward) => {
  const id = getRewardId(reward)
  return isItemId(id) || String(id).startsWith('TBD_')
}

const rewardIconStyle = (row) =>
  rectStyle(
    row.card[0] + ROW.circle.dx,
    row.card[1] + ROW.circle.dy,
    ROW.circle.size,
    ROW.circle.size,
  )

const btnClass = (row) => {
  if (row.status === 1) return 'can-receive'
  if (row.status === 2) return 'received'
  return 'not-done'
}

const btnText = (row) => {
  if (row.status === 1) return t('ann2026.milestone.claim')
  if (row.status === 2) return t('ann2026.milestone.claimed')
  return t('ann2026.milestone.notAchieved')
}

const onReceive = (row) => {
  if (row.status !== 1 || !row.rewardCode) return
  emit('receive', row.rewardCode)
}
</script>

<style lang="less" scoped>
// 区块范围：设计稿 1239 ~ 2920，内部子元素绝对定位，坐标 = 设计稿 top - 1239
.ann-milestone {
  position: relative;
  width: 752px;
  height: 1681px;
  background: transparent;
  overflow: hidden;

  > * {
    position: absolute;
  }
}

.ms-divider {
  left: 0;
  width: 752px;
  height: 74px;
  background-image: url('~@/assets/imgs/act/ann2026/common/divider.png');
  background-size: 752px 74px;
  background-repeat: no-repeat;

  &--top {
    top: 0; // 1239
  }

  &--bottom {
    top: 1584px; // 2823
  }
}

.ms-title-wrap {
  left: 22px;
  top: 145px; // 1384
  width: 709px;
  height: 103px;
  z-index: 4;

  .ms-title {
    width: 709px;
    height: auto;
    object-fit: contain;
  }
}

.ms-panel {
  left: 25px;
  top: 158px; // 1397
  width: 701px;
  height: 1403px;
  z-index: 1;
  background: url('~@/assets/imgs/act/ann2026/milestone/panel.png') -30px -16px no-repeat;
  background-size: 754px 1456px;

  .ms-panel-inner {
    position: absolute;
    left: 31px; // 56 - 25
    top: 78px; // 1475 - 1397
    width: 649px;
    height: 1293px;
    border-radius: 57px;
    background: #fff;
  }
}

.ms-track-base {
  left: 64px;
  top: 323.2px; // 1562.2
  width: 18px;
  height: 1139.6px;
  z-index: 2;
  background-image: url('~@/assets/imgs/act/ann2026/milestone/track-base.png');
  background-size: 100% 100%;
}

.ms-track-fill {
  left: 64px;
  top: 323.8px; // 1562.8
  width: 18px;
  height: 1138.1px;
  z-index: 2;
  background-image: url('~@/assets/imgs/act/ann2026/milestone/track-fill.png');
  background-size: 100% 100%;
  border-radius: 9px;
}

.ms-row-bg,
.ms-circle,
.ms-icon,
.ms-times,
.ms-name,
.ms-btn,
.ms-dot,
.ms-check {
  z-index: 2;
}

.ms-row-bg,
.ms-circle,
.ms-btn {
  background-size: 100% 100%;
  background-repeat: no-repeat;
}

.ms-name,
.ms-btn {
  display: flex;
  align-items: center;
  justify-content: center;
}

.ms-row-bg {
  background-image: url('~@/assets/imgs/act/ann2026/milestone/row-bg.png');
}

.ms-circle {
  background-image: url('~@/assets/imgs/act/ann2026/milestone/circle.png');
}

.ms-times {
  font-size: 20px;
  line-height: 1.05;
  font-weight: bold;
  text-align: center;
  color: #747474;

  &.achieved {
    color: #d85b00;
  }
}

.ms-name {
  font-size: 17px;
  font-weight: bold;
  color: #fff;
  text-align: center;
  padding: 0 10px;
  box-sizing: border-box;

  span {
    display: block;
  }

  span + span {
    margin-left: 5px;
    white-space: nowrap;
  }
}

.ms-icon {
  object-fit: contain;
}

.ms-btn {
  background-image: url('~@/assets/imgs/act/ann2026/milestone/btn-grey.png');
  font-size: 17px;
  line-height: 1.1;
  font-weight: bold;
  text-align: center;
  color: #fff;

  &.can-receive {
    background-image: url('~@/assets/imgs/act/ann2026/milestone/btn-claim.png');
    color: #1853d7;
    cursor: pointer;
  }
}

.ms-confetti {
  left: 28px;
  top: 64px; // 1303
  width: 108px;
  height: 132px;
  z-index: 3;
  background: url('~@/assets/imgs/act/ann2026/milestone/confetti.png') no-repeat;
  background-size: 108px 132px;
}
</style>
